<template>
  <DefaultLayout>
    <div class="agent-documents-container">
      <div class="header">
        <h3>Documents for Agent: {{ agentName }}</h3>
        <button class="btn btn-secondary" @click="goBackToAgents">Back to Agents</button>
      </div>
      <div class="content">
        <!-- Upload Document Form -->
        <div class="upload-document-form">
          <h6>Upload New Document</h6>
          <div class="form-row">
            <div class="form-group">
              <DInput
                :value="url"
                @input="(e) => { url = e.target.value; validUrl(); resetUploadedFileData(); }"
                label="Document URL"
                width="100%"
                placeholder="Enter document URL"
                :disabled="isSubmitting"
                :errorMessage="!!(url && !isValidUrl)"
                errMsg="Invalid URL (ex. https://example.com/)."
              />
            </div>

            <div class="separator">OR</div>

            <div class="form-group">
              <label for="documentFile">Upload Document</label>
              <input
                :disabled="isSubmitting"
                @change="uploadFile"
                ref="appFile"
                id="documentFile"
                class="form-control"
                type="file"
                accept=".docx,.pdf"
              />
            </div>
          </div>

          <button
            class="btn btn-primary flex m-auto mt-3"
            :disabled="(!file && !url) || isSubmitting || !isValidUrl"
            @click="onSubmit"
          >
            {{ isSubmitting ? "Uploading" : "Upload" }}
            <span
              v-if="isSubmitting"
              style="margin: 5px 0 0 8px"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
        </div>

        <!-- Documents List -->
        <div v-if="loading" class="text-center p-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        <div v-else-if="documents.length === 0" class="alert alert-info" role="alert">
          No documents found.
        </div>
        <table v-else class="table table-striped table-hover">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Type</th>
              <th scope="col">Date</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(doc, index) in documents" :key="doc.id || index">
              <th scope="row">{{ index + 1 }}</th>
              <td>{{ doc.name || 'Unnamed Document' }}</td>
              <td>{{ doc.type || 'Unknown' }}</td>
              <td>{{ formatDate(doc.created_at) }}</td>
              <td>
                <button class="btn btn-sm btn-primary mr-2" @click="viewDocument(doc)">View</button>
                <button class="btn btn-sm btn-danger" @click="confirmDelete(doc)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DModal :visible="deleteVisible" @update:visible="deleteVisible = $event">
      <template #title>Confirm Delete</template>
      <template #body>
        Are you sure you want to delete the document "{{ selectedDoc?.name || 'Unnamed Document' }}"?
        <div class="text-danger mt-2"><small>This action cannot be undone.</small></div>
      </template>
      <template #footer>
        <button type="button" class="btn btn-secondary cancel-button" @click="deleteVisible = false">Cancel</button>
        <button type="button" class="btn btn-danger action-btn" @click="deleteDocument" :disabled="deleting">
          <span v-if="deleting" class="spinner-sm mr-2" role="status" aria-hidden="true"></span>
          {{ deleting ? 'Deleting...' : 'Delete' }}
        </button>
      </template>
    </DModal>
  </DefaultLayout>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from 'vue-router';
import { useAgentsStore } from "@/stores/agents";
import { useDocumentsStore } from "@/stores/documents";
import DefaultLayout from "@/views/layouts/DefaultLayout.vue";
import useValidation from "@/utils/helpers/validations";
import { useToast } from "vue-toastification";
import { useCompanyStore } from "@/stores/company";

export default {
  name: 'AgentDocuments',
  components: {DefaultLayout},
  props: {
    agentId: {
      type: String,
      required: true
    },
    agentName: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const agentsStore = useAgentsStore();
    const documentsStore = useDocumentsStore();
    const companyStore = useCompanyStore();
    const router = useRouter();
    const toast = useToast();
    const { checkApkUrlValidation } = useValidation();

    const documents = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedDoc = ref(null);
    const deleting = ref(false);
    const deleteVisible = ref(false);

    const url = ref("");
    let file = ref(null);
    let appFile = ref(null);
    let fileError = ref(false);
    const isSubmitting = ref(false);
    let isValidUrl = ref(true);

    const fetchDocuments = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await agentsStore.fetchDocumentsByAgent(companyStore.companyId, props.agentId);
        documents.value = response?.data || [];
      } catch {
        error.value = 'Failed to load documents. Please try again later.';
      } finally {
        loading.value = false;
      }
    };

    const viewDocument = (doc) => {
      if (doc.type === "url" && doc.name) {
        window.open(doc.name, '_blank');
      } else if (doc.s3_url) {
        window.open(process.env.VUE_APP_S3_URL + doc.s3_url, '_blank');
      } else {
        console.error('Document does not have a valid URL', doc);
      }
    };

    const confirmDelete = (doc) => {
      selectedDoc.value = doc;
      deleteVisible.value = true;
    };

    const deleteDocument = async () => {
      if (!selectedDoc.value || !selectedDoc.value.id) {
        console.error('No document selected for deletion');
        return;
      }

      deleting.value = true;

      try {
        await agentsStore.deleteDocumentByAgent(companyStore.companyId, selectedDoc.value.id);
        documents.value = documents.value.filter(doc => doc.id !== selectedDoc.value.id);
        deleteVisible.value = false;
      } catch {
        // error toast handled by store
      } finally {
        deleting.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    };

    const goBackToAgents = () => {
      router.push('/agents');
    };

    const validUrl = () => {
      isValidUrl.value = true;
      if (url.value.length > 0) {
        isValidUrl.value = checkApkUrlValidation(url.value);
        return isValidUrl.value;
      }
    };

    const uploadFile = async (e) => {
      fileError.value = false;
      let selectedFile = e.target.files[0];

      let reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = () => {
        if (
          selectedFile.type !== "application/pdf" &&
          selectedFile.type !==
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          resetUploadedFileData();
          toast.error("Only (.pdf , .docx) extensions are allowed");
          return;
        }
        file.value = selectedFile;
      };
      reader.onerror = (evt) => {
        console.error(evt);
      };
      url.value = "";
    };

    const resetUploadedFileData = () => {
      file.value = null;
      appFile.value.value = null;
      fileError.value = false;
    };

    const onSubmit = async () => {
      isSubmitting.value = true;
      let formData = new FormData();
      !!file.value && formData.append("file", file.value);
      !!url.value && formData.append("url", url.value);
      formData.append("company_id", companyStore.companyId);
      formData.append("agent_id", props.agentId);
      formData.append("type", file.value ? "document" : "url");
      const hasFile = !!file.value;

      await documentsStore
        .updateAiData({
          formData,
          hasFile,
        })
        .finally(() => {
          isSubmitting.value = false;
          resetUploadedFileData();
        });
    };

    onMounted(() => {
      fetchDocuments();
    });

    return {
      documents,
      loading,
      error,
      formatDate,
      viewDocument,
      confirmDelete,
      deleteDocument,
      selectedDoc,
      deleting,
      deleteVisible,
      goBackToAgents,
      url,
      file,
      appFile,
      fileError,
      isSubmitting,
      isValidUrl,
      validUrl,
      uploadFile,
      resetUploadedFileData,
      onSubmit
    };
  }
}
</script>

<style scoped>
.agent-documents-container {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.content {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.upload-document-form {
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.form-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 15px;
}

.form-group {
  flex: 1;
  min-width: 200px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.separator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  font-weight: bold;
  color: #666;
  margin: 0 10px;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .separator {
    margin: 15px 0;
    padding: 5px 0;
    width: 100%;
    text-align: center;
  }
}
</style>
